import { useState } from 'react';
import { ReactSVG } from 'react-svg';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { DndContext, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { Tabs, SearchInput, Button, mock } from '@forma/forma-ui-kit';
import ColumnButton from './ColumnButton';
import CreateEditColumn from './CreateEditColumn';

import { ITemplateTableColumn } from 'interfaces/templates.interface';
import { IProductPropertiesItem } from 'interfaces/products.interface';
import { TVariableType } from 'interfaces/variables.interface';

import styles from './products-table-select-modal.module.css';

interface TableColumnsSelectProps {
  columns: ITemplateTableColumn[],
  properties?: IProductPropertiesItem[],
  onCreateColumn?: (data: Omit<IProductPropertiesItem, 'id'>) => void,
  onEditColumn?: (data: IProductPropertiesItem) => void,
  onRemoveColumn?: (id: string) => void,
  onSelect: (data: { id: string, name: string, type?: TVariableType, width?: string }) => void,
  onDeselect: (id: string) => void,
}

const TableColumnsSelect = ({
  columns, properties, onCreateColumn, onEditColumn, onRemoveColumn, onSelect, onDeselect
}: TableColumnsSelectProps) => {
  const { t } = useTranslation();
  const [editId, setEditId] = useState<string | null>(null);
  const editColumn = properties?.find(({ id }) => editId === id);
  const [isEditOpen, setEditOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState<string>('default');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeId = String(active.id);
    const overId = over ? String(over.id) : null;
    if (!activeId || !overId || activeId === overId) return null;

    // fixme: sorting
    // setColumns(prev => {
    //   const activeIndex = prev.findIndex(({ id }) => id === activeId);
    //   const overIndex = prev.findIndex(({ id }) => id === overId);

    //   return arrayMove(prev, activeIndex, overIndex);
    // });
  };

  const filteredColumns = search.length ? columns.filter(column => column.name.toLowerCase().includes(search.toLowerCase())) : columns;
  const filteredMock = mock.productTableColumns.filter(column => {
    const isFiltered = search ? (
      column.name.toLowerCase().includes(search.toLowerCase()) && !columns.find(({ id }) => column.id === id)
    ) : true;
    const isSelected = columns.some(({ id }) => column.id === id);
    return isFiltered && !isSelected;
  });

  const filteredCustom = properties?.filter(column => {
    const isFiltered = search ? (
      column.name.toLowerCase().includes(search.toLowerCase()) && !columns.find(({ id }) => column.id === id)
    ) : true;
    const isSelected = columns.some(({ id }) => column.id === id);
    return isFiltered && !isSelected;
  });

  return (
    <div className={classNames(styles.block, styles.left)}>
      <div className={styles.subtitle}>{t('table_columns')}</div>
      <div className={styles.search}>
        <SearchInput
          className={styles.searchInput}
          placeholder={t('uikit:search')}
          onSearch={setSearch}
        />
        {onCreateColumn && (
          <Button
            viewStyle="secondary"
            onClick={() => {
              setEditId(null);
              setEditOpen(true);
            }}
            className={classNames(styles.button, styles.buttonAdd)}
            icon={<ReactSVG src="/icons/plus.svg" wrapper="span" />}
            iconClassName={styles.buttonIcon}
          />
        )}
      </div>
      <div className={styles.columns}>
        {filteredColumns.length ? (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext items={columns} strategy={rectSortingStrategy}>
              {filteredColumns.map(column => (
                <ColumnButton
                  data={column}
                  onRemove={() => onDeselect(column.id)}
                  onEdit={
                    properties?.some(({ id }) => column.id === id) ? () => {
                      setEditId(column.id);
                      setEditOpen(true);
                    } : undefined
                  }
                  draggable
                  checked
                  key={column.id}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <div className={styles.columnsEmpty}>{t('select_columns')}</div>
        )}
      </div>
      <Tabs
        current={selectedTab}
        onChange={setSelectedTab}
        className={styles.tabs}
        disableScroll
        items={[
          {
            id: 'default',
            name: t('defaults'),
            children: <div className={styles.columns}>
              {filteredMock.map(column => (
                <ColumnButton
                  data={column}
                  onClick={() => onSelect({ id: column.id, name: column.name, type: column.type, width: column.width })}
                  key={column.id}
                />
              ))}
            </div>
          },
          {
            id: 'created',
            name: t('createds'),
            children: <div className={styles.columns}>
              {filteredCustom?.map(column => (
                <ColumnButton
                  data={column}
                  onClick={() => onSelect({ id: column.id, name: column.name, type: column.type, width: '50px' })}
                  onEdit={() => {
                    setEditId(column.id);
                    setEditOpen(true);
                  }}
                  key={column.id}
                />
              ))}
            </div>
          }
        ]}
      />

      <CreateEditColumn
        open={isEditOpen}
        onClose={setEditOpen}
        data={editColumn && { id: editColumn.id, name: editColumn.name, type: editColumn.type }}
        onAdd={onCreateColumn}
        onEdit={onEditColumn}
        onRemove={onRemoveColumn}
      />
    </div>
  );
};

export default TableColumnsSelect;
